/**
 * The catalogue of mascot frames the page uses — the same shape as shots.ts, and for the same
 * reason: the intrinsic width/height belong in one table rather than in every call site,
 * because without them the page reflows as each frame arrives.
 *
 * Every entry exists on disk under public/mascot as AVIF + WebP + PNG at the widths listed,
 * produced by landing/tools/build-mascot.mjs (`npm run mascot`) and catalogued with byte
 * counts in landing/todo/70-mascot.md.
 *
 * ⚠ THE CHARACTER CHANGED, AND THE GEOMETRY CHANGED WITH IT. The mascot is now a bear in a
 * zip hoodie (source: todo/Cryptico-personage/personage-3). The retired character was drawn
 * wide and squat — its figures were roughly as wide as they were tall. These are not: trimmed
 * aspect ratios run 0.39-0.61 (width to height), so the SAME width now yields a figure about
 * twice as tall. Every number below was re-derived from the box the element renders in; none
 * was carried over.
 *
 * FOUR KINDS, and the difference is real rather than cosmetic:
 *
 *   face   — head crop in a section head, a flat 192 css px;
 *   hero   — the one figure that overlaps the app frame, sized as a % of that frame;
 *   aside  — a figure standing beside a band of screenshots, sized by HEIGHT (see below);
 *   flank  — a figure in the empty margin beside the centred phone row.
 *
 * WHY `aside` IS SIZED BY HEIGHT. Three different poses stand in that slot — holding a phone,
 * walking, sitting with a laptop — with three different aspect ratios. A shared width would
 * have made the sitting one squat and the standing ones tower; a shared height makes them read
 * as the same character at the same distance. The CSS therefore sets `height` and lets the
 * width fall out, and the numbers here are what that height implies per frame.
 *
 * WHICH FRAME GOES WHERE IS THE OWNER'S, NOT THIS FILE'S. hero = 1.png, organize = 3.png,
 * platforms = 5.png were assigned directly (2026-08-27); calls, security and the three
 * portraits were chosen here to fit what is left to the tone of each section.
 *
 * ⚠ NEVER MIRROR A FIGURE. Every one of them carries the Cryptico "C" on the chest, and a
 * flipped copy prints the mark backwards. When a pose faces the wrong way for a slot, move the
 * element to the other side of the content — never the pixels.
 *
 * THE FIVE EXPRESSIONS DO NOT COLLAPSE INTO PAIRS the way the retired set did. Measured as a
 * strip at the display size on the real #0b0e13 background: a paw under the chin, a winked
 * eye, a symmetric open-eyed front, two closed happy arcs and a three-quarter turn are five
 * different silhouettes at 192px. The page places only two of them (thinking and wink); the
 * neutral portrait lost its slot with the waiting-list section.
 */

export type MascotKind = "face" | "hero" | "aside" | "flank";

export interface Mascot {
  /** File stem under /mascot — `face-wink` resolves to `/mascot/face-wink-192.avif` etc. */
  readonly name: string;
  readonly kind: MascotKind;
  /**
   * Intrinsic size of the 1x variant, in CSS pixels. This is an aspect-ratio declaration for
   * the <img> as much as a size: it is what stops the section jumping as the frame arrives.
   */
  readonly width: number;
  readonly height: number;
  /**
   * The srcset rungs on disk: the display size at 1x, 2x and 3x. A rung is missing where the
   * artwork ran out — the trimmed sources are 530-807 px wide, so `mascot-stand` has no true
   * 3x and tops out at the file's own 546.
   */
  readonly widths: readonly number[];
}

export const MASCOTS = {
  /**
   * Paw under the chin, brows down, gaze off to the side, mouth a flat line. The one frame of
   * the five that is not smiling, and the only one that does not look glib beside a list of
   * things the product will not do.
   */
  thinking: { name: "face-thinking", kind: "face", width: 192, height: 234, widths: [192, 384, 576] },
  /** One eye winked shut and the head tilted — the most legible silhouette of the set. */
  wink: { name: "face-wink", kind: "face", width: 192, height: 198, widths: [192, 384, 576] },
  /**
   * Square to the viewer, symmetric, both eyes open, a small closed-mouth smile. The safe
   * default. Currently unplaced: its slot was the waiting-list section, removed when the site
   * stopped collecting anything. Kept in the catalogue because the files are still on disk.
   */
  neutral: { name: "face-neutral", kind: "face", width: 192, height: 210, widths: [192, 384, 576] },

  /**
   * THE HERO FIGURE (owner's choice of frame): standing square to the viewer, both paws in the
   * hoodie pockets. It leans on the app frame's bottom-right corner — see .hero__mascot in
   * sections.css for what it may cover and why the size is what it is.
   *
   * 206x518 is the 1x rung, and the tallest ratio on the page by a distance: this frame is
   * 0.398 wide to tall, so its height is what every decision about it turns on.
   */
  stand: { name: "mascot-stand", kind: "hero", width: 206, height: 518, widths: [206, 412, 546] },

  /**
   * Holding a phone up in the raised paw, looking at the viewer over the top of it. It stands
   * to the LEFT of the calls screenshots, and it is in that band because of the prop: a phone
   * in the paw next to a group call and a ringing handset is the one pose in the set that says
   * something about the section it sits in. The pose is near-frontal, so it does not face away
   * from the content on its right.
   */
  phone: { name: "mascot-phone", kind: "aside", width: 112, height: 290, widths: [112, 224, 336] },
  /**
   * Striding to the viewer's right with a backpack, head turned back over the shoulder. It
   * stands to the LEFT of the privacy-settings frames, which is the side its direction wants:
   * from there it walks into the section rather than out of it.
   */
  walk: { name: "mascot-walk", kind: "aside", width: 132, height: 287, widths: [132, 264, 396] },
  /**
   * Sitting on a cube with a laptop on the lap (owner's choice for this band): someone sitting
   * down and sorting their chats into folders is what the section is about, and this is the
   * only seated pose in the set.
   *
   * ⚠ THE CUBE IS PART OF THE ARTWORK, not a background artefact — the figure sits on an
   * object rather than floating. Do not try to cut it out; it is what gives the pose a floor.
   */
  laptop: { name: "mascot-laptop", kind: "aside", width: 176, height: 287, widths: [176, 352, 528] },

  /**
   * Standing with a chat card floating above the open palm (owner's choice for this band). It
   * goes in the EMPTY MARGIN to the left of the centred phone row, never in the row: that row
   * is centred by an explicit owner decision and a figure taking a track would drag it sideways.
   *
   * The card sits to the viewer's RIGHT of the figure, which is what fixes the side: from the
   * left margin the brightest element in the frame points INTO the section, toward the phones.
   */
  present: { name: "mascot-present", kind: "flank", width: 132, height: 239, widths: [132, 264, 396] },
} as const satisfies Record<string, Mascot>;

export function srcSet(mascot: Mascot, extension: "avif" | "webp"): string {
  return mascot.widths
    .map((width) => `/mascot/${mascot.name}-${width}.${extension} ${width}w`)
    .join(", ");
}

/** What `src` points at for a browser with neither AVIF nor WebP: the 1x rung. */
export function fallbackSrc(mascot: Mascot): string {
  return `/mascot/${mascot.name}-${mascot.width}.png`;
}

/**
 * `sizes` is the width the element actually occupies, so a dense screen reaches up the ladder
 * instead of stretching the 1x file. It comes from the same number the ladder was built from,
 * which is what keeps the two from drifting apart: change the CSS box and both follow.
 */
export function sizesFor(mascot: Mascot): string {
  return `${mascot.width}px`;
}
